import { Component, type ErrorInfo, type ReactNode } from 'react';

import { reportError } from '@src/utils/observability/report-error';

type ErrorBoundaryProps = {
  children: ReactNode;
  /** Rendered when a descendant throws; renders nothing when omitted. */
  fallback?: ReactNode;
  /** Extra attributes attached to the reported error (e.g. location). */
  attributes?: Record<string, unknown>;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

/**
 * Reports descendant render errors to Dash0 and renders `fallback` instead.
 * `hasError` is sticky: the boundary keeps rendering `fallback` until it is
 * remounted — pass a changing `key` to reset it after an error.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportError(error, { componentStack: info.componentStack, attributes: this.props.attributes });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }

    return this.props.children;
  }
}
